import type {
  DsdEditorCompanyNameOccurrence,
  DsdEditorCompanyNameReference,
} from "@/apis/dsd-editor/route";
import {
  AutoDismissWarningTooltip,
  EmptyPanelMessage,
  ReviewOnlyToggle,
} from "./note-reference-review-panel-ui";
import { cn } from "@/lib/utils";
import { Building2, ChevronRight, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";

const hasNeedsConfirmation = (reference: DsdEditorCompanyNameReference) =>
  reference.occurrences.some((occurrence) => occurrence.needsUnification);

const NoMatchingItems = () => (
  <div className="flex min-h-[160px] items-center justify-center rounded-[8px] border border-line-normal bg-background-base px-5 text-center">
    <p className="body-2-normal-medium text-label-alternative">
      해당하는 항목이 없습니다.
    </p>
  </div>
);

/** 목록 뷰 행 — 대표명 + (확인 필요면 경고) + 등장 횟수. 위치·표기는 상세에서 */
const RepresentativeListItem = ({
  reference,
  onSelect,
}: {
  reference: DsdEditorCompanyNameReference;
  onSelect: (representative: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onSelect(reference.representative)}
    className="flex w-full items-center justify-between gap-2 rounded-[8px] border border-line-normal bg-background-base p-3 text-left transition-colors hover:border-label-normal hover:bg-background-neutral-alternative"
  >
    <span className="min-w-0 break-words body-2-normal-medium text-label-normal">
      {reference.representative || "(대표 표기 없음)"}
    </span>
    <span className="flex shrink-0 items-center gap-1.5">
      {hasNeedsConfirmation(reference) && (
        <TriangleAlert
          role="img"
          aria-label="확인 필요"
          className="size-4 shrink-0 text-rose-500"
        />
      )}
      <span className="caption-1-medium text-label-alternative">
        등장 {reference.occurrences.length}회
      </span>
      <ChevronRight className="size-4 text-label-alternative" />
    </span>
  </button>
);

const CompanyNameOccurrenceItem = ({
  occurrence,
  locationLabel,
  isSelected,
  warnTooltipKey,
  onSelect,
}: {
  occurrence: DsdEditorCompanyNameOccurrence;
  locationLabel?: string;
  isSelected: boolean;
  /** 경고 위 검은 툴팁 재노출 키 — 첫 확인 필요 표기만 받는다 (매칭 탭과 동일) */
  warnTooltipKey: string | null;
  onSelect: (occurrence: DsdEditorCompanyNameOccurrence) => void;
}) => (
  <button
    type="button"
    onClick={() => onSelect(occurrence)}
    className={cn(
      "flex w-full items-center justify-between gap-3 rounded-[8px] border px-3 py-2 text-left transition-colors",
      isSelected
        ? "border-primary-base bg-background-neutral-alternative"
        : "border-line-normal bg-background-base hover:bg-decorate-neutral/6",
    )}
  >
    <div className="flex min-w-0 flex-col gap-1">
      {locationLabel && (
        <p className="caption-1-medium truncate text-label-alternative">
          {locationLabel}
        </p>
      )}
      <p className="body-2-normal-medium break-words text-label-normal">
        {occurrence.surface || "(빈 값)"}
      </p>
    </div>
    {occurrence.needsUnification && (
      <AutoDismissWarningTooltip
        label="표기를 확인해 주세요."
        openKey={warnTooltipKey}
        align="end"
      >
        <TriangleAlert className="size-4 shrink-0 text-rose-500" />
      </AutoDismissWarningTooltip>
    )}
  </button>
);

/** 대표명 카드 — 매칭 MainCellCard 미러. 스크롤 밖에 고정 */
const RepresentativeCard = ({ representative }: { representative: string }) => (
  <section className="flex min-w-0 shrink-0 items-center gap-2 px-4 pt-3">
    <div className="shrink-0 rounded-[6px] bg-blue-100 p-2">
      <Building2 className="size-3 text-blue-500" />
    </div>
    <div className="min-w-0">
      <p className="caption-1-medium text-label-alternative">대표 표기</p>
      <p className="body-2-normal-medium break-words text-label-normal">
        {representative || "(대표 표기 없음)"}
      </p>
    </div>
  </section>
);

/** 상세 뷰 표기 목록. 토글은 목록 뷰와 같은 상태를 공유 */
const OccurrenceList = ({
  reference,
  locationByOccurrenceId,
  selectedOccurrenceId,
  needsConfirmationOnly,
  onSelectOccurrence,
}: {
  reference: DsdEditorCompanyNameReference;
  locationByOccurrenceId: ReadonlyMap<string, string>;
  selectedOccurrenceId: string | null;
  needsConfirmationOnly: boolean;
  onSelectOccurrence: (occurrence: DsdEditorCompanyNameOccurrence) => void;
}) => {
  const shownOccurrences = needsConfirmationOnly
    ? reference.occurrences.filter((occurrence) => occurrence.needsUnification)
    : reference.occurrences;
  const firstWarnIndex = shownOccurrences.findIndex(
    (occurrence) => occurrence.needsUnification,
  );

  // 토글 ON인데 남는 표기가 없다 = 이 기업명의 표기는 모두 같다.
  // 매칭 패널의 "모든 항목을 검토했어요"와 같은 모양 (테두리 없는 문구). 글자만 기업명과 같은 크기,
  // 색은 그쪽 text-neutral-700에 가장 가까운 시맨틱 토큰
  if (shownOccurrences.length === 0)
    return (
      <p className="flex h-[200px] items-center justify-center body-2-normal-regular text-label-neutral">
        모든 표기가 동일합니다.
      </p>
    );

  // pt-4: 첫 표기에 뜨는 경고 툴팁(아이콘 위 약 40px)이 위 토글 줄을 덮지 않게 띄운다
  return (
    <div className="flex flex-col gap-2 pt-4">
      {shownOccurrences.map((occurrence, index) => (
        <CompanyNameOccurrenceItem
          key={occurrence.occurrenceId}
          occurrence={occurrence}
          locationLabel={locationByOccurrenceId.get(occurrence.occurrenceId)}
          isSelected={selectedOccurrenceId === occurrence.occurrenceId}
          warnTooltipKey={
            index === firstWarnIndex ? reference.representative : null
          }
          onSelect={onSelectOccurrence}
        />
      ))}
    </div>
  );
};

/** 기업명 확인 패널 — 머리(안내 카드)는 화면이 넣는다 (교정 목록과 같은 방식) */
export const CompanyNameReviewPanel = ({
  header,
  references,
  locationByOccurrenceId,
  selectedRepresentative,
  selectedOccurrenceId,
  needsConfirmationOnly,
  onNeedsConfirmationOnlyChange,
  onSelectRepresentative,
  onSelectOccurrence,
}: {
  /** 상세(대표명 선택)면 true — 뒤로 버튼은 머리가 단다 */
  header: (isDetail: boolean) => ReactNode;
  references: DsdEditorCompanyNameReference[];
  locationByOccurrenceId: ReadonlyMap<string, string>;
  selectedRepresentative: string | null;
  selectedOccurrenceId: string | null;
  needsConfirmationOnly: boolean;
  onNeedsConfirmationOnlyChange: (value: boolean) => void;
  onSelectRepresentative: (representative: string) => void;
  onSelectOccurrence: (occurrence: DsdEditorCompanyNameOccurrence) => void;
}) => {
  const activeReference = selectedRepresentative
    ? references.find(
        (reference) => reference.representative === selectedRepresentative,
      )
    : undefined;
  const needsConfirmationReferences = references.filter(hasNeedsConfirmation);
  const shownReferences = needsConfirmationOnly
    ? needsConfirmationReferences
    : references;

  return (
    <aside className="h-full min-h-0 bg-background-base">
      <div className="flex h-full flex-col">
        {header(!!activeReference)}
        {references.length === 0 ? (
          <EmptyPanelMessage
            title="검토할 기업명이 없습니다."
            description="문서에서 기업명을 찾지 못했어요."
          />
        ) : (
          <>
            {activeReference && (
              <RepresentativeCard
                representative={activeReference.representative}
              />
            )}
            {/* 목록: 개수(좌) + 토글(우) — 매칭·합계 탭과 같은 자리. 상세: 토글만 */}
            <div className="flex shrink-0 items-center justify-end gap-2 px-3 pb-2 pt-3">
              {!activeReference && (
                // 토글 ON이면 총 개수를 빼고 확인 필요 개수만 — 목록에 남는 행 수와 헤더 숫자를 맞추려는 의도.
                // 매칭·합계·교정 탭은 토글과 무관하게 '총 N개'라 이 점은 탭마다 다르다.
                // 빨강(status-negative)은 확인할 것이 있을 때만 — 0개면 기본색
                <p className="mr-auto label-2-medium text-label-normal">
                  {!needsConfirmationOnly && (
                    <>총 기업명 {references.length}개 · </>
                  )}
                  <span
                    className={cn(
                      needsConfirmationReferences.length > 0 &&
                        "text-status-negative",
                    )}
                  >
                    확인 필요 기업명 {needsConfirmationReferences.length}개
                  </span>
                </p>
              )}
              <ReviewOnlyToggle
                active={needsConfirmationOnly}
                onChange={onNeedsConfirmationOnlyChange}
              />
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
              {activeReference ? (
                <OccurrenceList
                  reference={activeReference}
                  locationByOccurrenceId={locationByOccurrenceId}
                  selectedOccurrenceId={selectedOccurrenceId}
                  needsConfirmationOnly={needsConfirmationOnly}
                  onSelectOccurrence={onSelectOccurrence}
                />
              ) : shownReferences.length === 0 ? (
                <NoMatchingItems />
              ) : (
                <div className="flex flex-col gap-1.5">
                  {shownReferences.map((reference) => (
                    <RepresentativeListItem
                      key={reference.representative}
                      reference={reference}
                      onSelect={onSelectRepresentative}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </aside>
  );
};
